/* eslint-disable */
import React, { useEffect, useState } from 'react';
import {
  Image as ImageIcon,
  UploadCloud,
  Search,
  Folder,
  Trash2,
  Copy,
  Eye,
  X,
  ExternalLink,
  FileText,
} from 'lucide-react';
import axiosClient from '../api/axiosClient';
import toast from 'react-hot-toast';

export default function MediaLibrary() {
  const [files, setFiles] = useState([]);
  const [selectedFolder, setSelectedFolder] = useState('');
  const [search, setSearch] = useState('');
  const [uploading, setUploading] = useState(false);
  const [previewFile, setPreviewFile] = useState(null);
  const [isDragging, setIsDragging] = useState(false);

  useEffect(() => {
    fetchMedia();
  }, [selectedFolder, search]);

  const fetchMedia = async () => {
    try {
      const res = await axiosClient.get(`/media?folder=${selectedFolder}&search=${search}`);
      setFiles(res.data.files || []);
    } catch (err) {
      toast.error('Failed to load media files.');
    }
  };

  const handleUploadFiles = async (fileList) => {
    if (!fileList || fileList.length === 0) return;

    const formData = new FormData();
    for (let i = 0; i < fileList.length; i++) {
      formData.append('files', fileList[i]);
    }
    formData.append('folder', selectedFolder || 'uncategorized');

    setUploading(true);
    try {
      await axiosClient.post('/media/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Files uploaded successfully!');
      fetchMedia();
    } catch (err) {
      toast.error('Upload failed.');
    } finally {
      setUploading(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleUploadFiles(e.dataTransfer.files);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Delete this file permanently?')) {
      try {
        await axiosClient.delete(`/media/${id}`);
        toast.success('File deleted.');
        if (previewFile?.id === id) setPreviewFile(null);
        fetchMedia();
      } catch (err) {
        toast.error('Failed to delete file.');
      }
    }
  };

  const copyUrl = (url) => {
    navigator.clipboard.writeText(url);
    toast.success('Media URL copied to clipboard!');
  };

  return (
    <div
      className="space-y-6 animate-in fade-in duration-300"
      onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
      onDragLeave={() => setIsDragging(false)}
      onDrop={handleDrop}
    >
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-black text-white tracking-tight flex items-center space-x-2">
            <ImageIcon className="text-blue-500" />
            <span>Media Library & Assets</span>
          </h1>
          <p className="text-xs text-slate-400 mt-1">
            Upload, preview, organize, and copy asset URLs for website content and blogs.
          </p>
        </div>

        <label className="px-4 py-2.5 rounded-xl font-bold text-xs bg-blue-600 hover:bg-blue-500 text-white shadow-lg shadow-blue-600/30 flex items-center space-x-2 cursor-pointer transition-all self-start sm:self-auto">
          <UploadCloud size={16} />
          <span>{uploading ? 'Uploading...' : 'Upload Media'}</span>
          <input type="file" multiple onChange={(e) => handleUploadFiles(e.target.files)} className="hidden" />
        </label>
      </div>

      {/* Drag overlay notice */}
      {isDragging && (
        <div className="border-2 border-dashed border-blue-500 bg-blue-950/40 rounded-3xl p-8 text-center text-blue-300 animate-pulse">
          <UploadCloud size={40} className="mx-auto mb-2" />
          <p className="font-bold text-sm">Drop images and files here to upload instantly</p>
        </div>
      )}

      {/* Controls */}
      <div className="flex flex-col sm:flex-row items-center justify-between gap-4 bg-slate-900/60 p-4 rounded-2xl border border-slate-800">
        <div className="flex items-center space-x-1.5 overflow-x-auto w-full sm:w-auto pb-1 sm:pb-0">
          <Folder className="text-slate-500 mr-1 flex-shrink-0" size={16} />
          <button
            onClick={() => setSelectedFolder('')}
            className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all whitespace-nowrap ${
              selectedFolder === '' ? 'bg-blue-600 text-white shadow-md' : 'text-slate-400 hover:bg-slate-800'
            }`}
          >
            All Media
          </button>
          {['uncategorized', 'services', 'testimonials', 'portfolio', 'blog'].map((f) => (
            <button
              key={f}
              onClick={() => setSelectedFolder(f)}
              className={`px-3 py-1.5 rounded-xl text-xs font-bold capitalize transition-all whitespace-nowrap ${
                selectedFolder === f ? 'bg-blue-600 text-white shadow-md' : 'text-slate-400 hover:bg-slate-800'
              }`}
            >
              {f}
            </button>
          ))}
        </div>

        <div className="relative w-full sm:w-72">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={15} />
          <input
            type="text"
            placeholder="Search filenames..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 pr-4 py-2 rounded-xl border border-slate-800 bg-slate-950 text-white text-xs placeholder-slate-500 focus:outline-none focus:border-blue-500"
          />
        </div>
      </div>

      {/* Grid */}
      {files.length === 0 ? (
        <div className="text-center py-16 bg-slate-900/40 rounded-2xl border border-slate-800/60">
          <ImageIcon className="mx-auto text-slate-600 mb-3" size={40} />
          <h3 className="text-sm font-bold text-slate-300">No media files found</h3>
          <p className="text-xs text-slate-500 mt-1">Upload images, icons, or PDF documents to your storage.</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
          {files.map((file) => (
            <div
              key={file.id}
              className="bg-slate-900 border border-slate-800/80 rounded-2xl overflow-hidden group hover:border-blue-500/60 transition-all flex flex-col justify-between shadow-lg"
            >
              <div className="h-32 bg-slate-950 relative overflow-hidden flex items-center justify-center p-2">
                {file.mimeType?.startsWith('image/') || file.url?.match(/\.(jpeg|jpg|gif|png|webp|svg)/i) ? (
                  <img src={file.url} alt={file.originalName} className="object-cover w-full h-full rounded-xl" />
                ) : (
                  <FileText className="text-slate-600 w-10 h-10" />
                )}

                {/* Overlay actions */}
                <div className="absolute inset-0 bg-slate-950/80 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center space-x-1.5 p-2 backdrop-blur-xs">
                  <button
                    onClick={() => setPreviewFile(file)}
                    className="p-1.5 rounded-lg bg-slate-800 text-white hover:bg-blue-600 transition-colors"
                    title="Preview"
                  >
                    <Eye size={15} />
                  </button>
                  <button
                    onClick={() => copyUrl(file.url)}
                    className="p-1.5 rounded-lg bg-slate-800 text-blue-400 hover:bg-slate-700 transition-colors"
                    title="Copy URL"
                  >
                    <Copy size={15} />
                  </button>
                  <button
                    onClick={() => handleDelete(file.id)}
                    className="p-1.5 rounded-lg bg-slate-800 text-rose-400 hover:bg-rose-600/30 transition-colors"
                    title="Delete"
                  >
                    <Trash2 size={15} />
                  </button>
                </div>
              </div>

              <div className="p-3 text-[11px] space-y-0.5 bg-slate-900 border-t border-slate-800/60">
                <p className="font-bold text-white truncate" title={file.originalName}>
                  {file.originalName}
                </p>
                <div className="flex items-center justify-between text-[10px] text-slate-500">
                  <span className="uppercase">{Math.round(file.size / 1024)} KB</span>
                  <span className="capitalize">{file.folder}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Media Preview Modal */}
      {previewFile && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-in fade-in">
          <div className="bg-slate-900 border border-slate-800 rounded-3xl w-full max-w-2xl max-h-[90vh] flex flex-col shadow-2xl overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-800 flex items-center justify-between">
              <h2 className="text-base font-bold text-white truncate max-w-md">{previewFile.originalName}</h2>
              <button
                onClick={() => setPreviewFile(null)}
                className="p-1.5 rounded-xl text-slate-400 hover:text-white hover:bg-slate-800"
              >
                <X size={18} />
              </button>
            </div>

            <div className="p-6 overflow-y-auto flex flex-col items-center justify-center bg-slate-950">
              {previewFile.mimeType?.startsWith('image/') || previewFile.url?.match(/\.(jpeg|jpg|gif|png|webp|svg)/i) ? (
                <img
                  src={previewFile.url}
                  alt={previewFile.originalName}
                  className="max-h-96 max-w-full object-contain rounded-xl shadow-lg"
                />
              ) : (
                <div className="p-12 text-center text-slate-400">
                  <FileText size={64} className="mx-auto mb-2 text-slate-600" />
                  <p>Document preview not supported directly.</p>
                </div>
              )}
            </div>

            <div className="p-6 border-t border-slate-800 bg-slate-900 space-y-3 text-xs">
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 text-slate-400">
                <div>
                  <span className="block text-[11px]">File Size</span>
                  <span className="text-white font-bold">{Math.round(previewFile.size / 1024)} KB</span>
                </div>
                <div>
                  <span className="block text-[11px]">Folder</span>
                  <span className="text-white font-bold capitalize">{previewFile.folder}</span>
                </div>
                <div>
                  <span className="block text-[11px]">MIME Type</span>
                  <span className="text-white font-bold">{previewFile.mimeType}</span>
                </div>
              </div>

              <div>
                <label className="block text-[11px] text-slate-400 mb-1">Direct Asset URL</label>
                <div className="flex items-center space-x-2">
                  <input
                    type="text"
                    readOnly
                    value={previewFile.url}
                    className="flex-1 bg-slate-950 border border-slate-800 rounded-xl px-3 py-2 text-xs text-blue-400 font-mono"
                  />
                  <button
                    onClick={() => copyUrl(previewFile.url)}
                    className="px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-xl font-bold flex items-center space-x-1"
                  >
                    <Copy size={14} />
                    <span>Copy</span>
                  </button>
                  <a
                    href={previewFile.url}
                    target="_blank"
                    rel="noreferrer"
                    className="p-2 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-xl"
                    title="Open in new tab"
                  >
                    <ExternalLink size={16} />
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
